import { HttpStatusCode } from 'axios';
import {
  MetadataRunConditionDto,
  MetadataRunConditionOperator,
} from '@/Domain/api/Shared/dto/MetadataRunCondition.dto';
import { ActionTypeEnum, AppActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';
import { ToolCallSeverity } from '@/Domain/utils/toolCallSeverity';

type GenericActionDto = {
  name: string;
  description: string;
  is_active: boolean;
  is_valid: boolean;
  /** Names of the failed validation checks, so the UI can say why an
   *  action is incomplete. Stable ids mapped to copy via
   *  `actions_page.validation.*`. Empty when valid. */
  validation_issues?: string[];
  icon: string;
  icon_color: string;
};

export type ActionDto = GenericActionDto & {
  id: number;
  type: ActionTypeEnum;
};

export type PatchActionDto = {
  id: number;
  is_active?: boolean;
  icon?: string;
  icon_color?: string;
};

export type AppActionDto = GenericActionDto & {
  id?: number;
  type: AppActionTypeEnum;
};

export type ResponseFilterDto = {
  id: number | string;
  json_path: string;
};

export type MetadataCaptureDto = {
  id: number | string;
  json_path: string;
  metadata_key: string;
};

export type RunConditionOperator = MetadataRunConditionOperator;
export type RunConditionDto = MetadataRunConditionDto;

export type HttpActionExecutionMode = 'ai_decides' | 'before_first_reply' | 'when_metadata_matches';

export type HttpActionDto = ActionDto & {
  url: string;
  type: ActionTypeEnum.HttpRequest;
  method: HttpMethodEnum;
  headers: ApiDataItemDto[];
  body_items: ApiDataItemDto[];
  body_json: Record<string, any>;
  query_items: ApiDataItemDto[];
  path_items: { id: number | null; key: string; content: null; parameter_id: number | null }[];
  parameters: ParameterDto[];
  thinking_message: string | null;
  execution_mode: HttpActionExecutionMode;
  response_filters: ResponseFilterDto[];
  response_metadata_captures: MetadataCaptureDto[];
  run_conditions: RunConditionDto[];
};

type HttpRequestAIActionFromTextCitation = {
  url: string;
  description: string;
};

export type HttpActionFromTextDto = {
  uuid: string;
  http_request_ai_action: HttpActionDto | null;
  citations: HttpRequestAIActionFromTextCitation[] | null;
  explainer_message: string;
  outcome?: 'created' | 'needs_input' | 'error';
  suggested_builtin_action?: 'human_handoff' | null;
};

export type TestParamDto = {
  id: string | number;
  name: string;
  value: string | number | boolean;
  type: ParameterTypeEnum;
};

export type ApiDataItemDto = {
  id: number | string;
  name: string;
  // Headers only: a write-only value the API returns as "[redacted]". Echoing the
  // mask back on save keeps the stored secret; it is never readable from here.
  is_secret?: boolean;
} & ({ value: string; content?: never } | { content: string; value?: never });

export type ParameterDto = {
  id: number;
  name: string;
  description: string;
  type: ParameterTypeEnum;
  default_value: string | boolean; // TOOD: establish hwo to pass bool as true/false of as "1" and "0"
  is_required: boolean;
};

export type TestResponse = {
  response: any;
  http_code: HttpStatusCode | null;
  bash_curl_command?: string;
};

export enum HttpRequestAIActionFromTextMode {
  CREATED = 'created',
  FAILED = 'failed',
}

export interface HttpRequestAIActionFromTextEmitProps {
  scenario_id: string;
  process_id: number;
  mode: HttpRequestAIActionFromTextMode;
  data: HttpActionFromTextDto;
}

export type AIActionUsageStatsDto = {
  ai_action_id: number;
  total_calls: number;
  success_count: number;
  warning_count: number;
  error_count: number;
  success_rate: number;
  avg_duration_ms: number | null;
  last_called_at: string | null;
};

export type AIActionUsageStatsResponseDto = {
  stats: Record<string, AIActionUsageStatsDto>;
};

export type AIActionCallDto = {
  id: number;
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_output_preview: string;
  status: 'success' | 'error';
  severity?: ToolCallSeverity;
  http_status_code: number | null;
  duration_ms: number | null;
  error_message: string;
  request_url: string | null;
  created_at: string;
  conversation_id: string;
};

export type AIActionCallsResponseDto = {
  calls: AIActionCallDto[];
  has_more: boolean;
};

export type AIActionHealthStatus = 'ok' | 'failing' | 'not_running' | 'needs_reconnect';

export type AIActionSkipReason =
  | 'missing_values'
  | 'invalid_configuration'
  | 'secret_unreadable'
  | 'server_unreachable'
  | 'needs_reconnect';

export type AIActionLastFailureDto = {
  http_status_code: number | null;
  error_message: string;
  created_at: string;
};

export type AIActionHealthDto = {
  ai_action_id: number;
  name: string;
  health: AIActionHealthStatus;
  execution_mode: HttpActionExecutionMode | null;
  recent_calls: number;
  recent_failed_calls: number;
  skipped_conversations: number;
  skip_reason: AIActionSkipReason | null;
  missing_values: string[];
  last_skipped_at: string | null;
  last_failure: AIActionLastFailureDto | null;
};

export type AIActionHealthResponseDto = {
  window_days: number;
  attention_count: number;
  actions: AIActionHealthDto[];
};

export type AIActionSkipDto = {
  conversation_id: string;
  reason: AIActionSkipReason;
  missing_values: string[];
  first_skipped_at: string;
  last_skipped_at: string;
};

export type AIActionSkipsResponseDto = {
  skips: AIActionSkipDto[];
  has_more: boolean;
};

export type ObservedMetadataKeyDto = {
  key: string;
  example_value: string | null;
  occurrence_count: number;
  has_scalar_value?: boolean;
  is_memory_key: boolean;
};
